import { isPlatformBrowser } from '@angular/common';
import {
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  OnInit,
  output,
  PLATFORM_ID,
  Renderer2,
  signal,
} from '@angular/core';

class SharedIntersectionObserver {
  private static instance: SharedIntersectionObserver | null = null;
  private observer: IntersectionObserver | null = null;
  private callbacks = new Map<Element, () => void>();
  private readonly isBrowser: boolean;

  private constructor() {
    this.isBrowser = typeof window !== 'undefined' && 'IntersectionObserver' in window;
  }

  static getInstance(): SharedIntersectionObserver {
    if (!SharedIntersectionObserver.instance) {
      SharedIntersectionObserver.instance = new SharedIntersectionObserver();
    }
    return SharedIntersectionObserver.instance;
  }

  observe(element: Element, callback: () => void): void {
    if (!this.isBrowser) {
      return;
    }

    if (!this.observer) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              const cb = this.callbacks.get(entry.target);
              if (cb) {
                cb();
              }
            }
          });
        },
        {
          root: null,
          rootMargin: '200px',
          threshold: 0.01,
        },
      );
    }

    this.callbacks.set(element, callback);
    this.observer.observe(element);
  }

  unobserve(element: Element): void {
    if (!this.isBrowser) {
      return;
    }

    this.callbacks.delete(element);
    this.observer?.unobserve(element);
  }
}

@Directive({
  selector: '[lazyBgLoader]',
  standalone: true,
})
export class LazyBgLoaderDirective implements OnInit, OnDestroy {
  lazyBgLoader = input<string>('');
  imageLoaded = output<void>();

  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private platformId = inject(PLATFORM_ID);

  private sharedObserver = SharedIntersectionObserver.getInstance();
  private isLoaded = false;
  private isDestroyed = false;
  private overlayElement: HTMLElement | null = null;
  private spinnerElement: HTMLElement | null = null;
  private currentSrc = '';
  private initialized = signal(false);

  private readonly isBrowser: boolean;

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);

    effect(() => {
      const newSrc = this.lazyBgLoader();
      if (this.initialized() && newSrc !== this.currentSrc) {
        this.resetLoading();
        if (this.isBrowser) {
          this.setupObserver();
          requestAnimationFrame(() => {
            if (!this.isDestroyed && !this.isLoaded) {
              this.applyBackgroundImage();
            }
          });
        } else {
          this.renderStaticBackground();
        }
      }
    });
  }

  ngOnInit(): void {
    this.setupElementStyle();
    if (!this.isBrowser) {
      this.renderStaticBackground();
      this.initialized.set(true);
      return;
    }

    this.showSpinner();
    this.setupObserver();
    this.initialized.set(true);
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.sharedObserver.unobserve(this.el.nativeElement);
    this.removeSpinner();
  }

  private resetLoading(): void {
    this.isLoaded = false;
    this.currentSrc = '';

    if (this.overlayElement) {
      this.renderer.setStyle(this.overlayElement, 'opacity', '0');
      this.renderer.removeStyle(this.overlayElement, 'background-image');
    }

    this.removeSpinner();
    this.showSpinner();
  }

  private setupElementStyle(): void {
    this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');
    this.renderer.setStyle(this.el.nativeElement, 'background-color', '#1a1a1a');
    this.renderer.setStyle(this.el.nativeElement, 'display', 'block');
    this.renderer.setStyle(this.el.nativeElement, 'width', '100%');
    this.renderer.setStyle(this.el.nativeElement, 'height', '100%');

    this.overlayElement = this.renderer.createElement('div');
    this.renderer.addClass(this.overlayElement, 'bg-image-overlay');
    this.renderer.setStyle(this.overlayElement, 'position', 'absolute');
    this.renderer.setStyle(this.overlayElement, 'top', '0');
    this.renderer.setStyle(this.overlayElement, 'left', '0');
    this.renderer.setStyle(this.overlayElement, 'right', '0');
    this.renderer.setStyle(this.overlayElement, 'bottom', '0');
    this.renderer.setStyle(this.overlayElement, 'background-size', 'cover');
    this.renderer.setStyle(this.overlayElement, 'background-position', 'center');
    this.renderer.setStyle(this.overlayElement, 'background-repeat', 'no-repeat');
    this.renderer.setStyle(this.overlayElement, 'opacity', '0');
    this.renderer.setStyle(this.overlayElement, 'transition', 'opacity 0.5s ease-in-out');
    this.renderer.setStyle(this.overlayElement, 'pointer-events', 'none');
    this.renderer.appendChild(this.el.nativeElement, this.overlayElement);
  }

  private setupObserver(): void {
    this.sharedObserver.unobserve(this.el.nativeElement);
    this.sharedObserver.observe(this.el.nativeElement, () => this.applyBackgroundImage());
  }

  private showSpinner(): void {
    if (this.spinnerElement) {
      return;
    }

    this.spinnerElement = this.renderer.createElement('div');
    this.renderer.setStyle(this.spinnerElement, 'position', 'absolute');
    this.renderer.setStyle(this.spinnerElement, 'top', '50%');
    this.renderer.setStyle(this.spinnerElement, 'left', '50%');
    this.renderer.setStyle(this.spinnerElement, 'transform', 'translate(-50%, -50%)');
    this.renderer.setStyle(this.spinnerElement, 'width', '24px');
    this.renderer.setStyle(this.spinnerElement, 'height', '24px');
    this.renderer.setStyle(this.spinnerElement, 'border', '2px solid rgba(255, 255, 255, 0.2)');
    this.renderer.setStyle(this.spinnerElement, 'border-top-color', 'rgba(255, 255, 255, 0.6)');
    this.renderer.setStyle(this.spinnerElement, 'border-radius', '50%');
    this.renderer.setStyle(this.spinnerElement, 'animation', 'lazyBgSpin 0.8s linear infinite');
    this.renderer.setStyle(this.spinnerElement, 'pointer-events', 'none');

    this.injectSpinnerKeyframes();
    this.renderer.appendChild(this.el.nativeElement, this.spinnerElement);
  }

  private injectSpinnerKeyframes(): void {
    const styleId = 'lazy-bg-loader-keyframes';
    if (typeof document !== 'undefined' && !document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `@keyframes lazyBgSpin { to { transform: translate(-50%, -50%) rotate(360deg); } }`;
      document.head.appendChild(style);
    }
  }

  private removeSpinner(): void {
    if (this.spinnerElement) {
      this.spinnerElement.remove();
      this.spinnerElement = null;
    }
  }

  private applyBackgroundImage(): void {
    const bgUrl = this.lazyBgLoader();
    if (this.isLoaded || !bgUrl || !this.overlayElement) {
      return;
    }

    this.sharedObserver.unobserve(this.el.nativeElement);

    if (!this.isBrowser) {
      this.renderer.setStyle(this.overlayElement, 'background-image', `url('${ bgUrl }')`);
      this.renderer.setStyle(this.overlayElement, 'opacity', '1');
      this.imageLoaded.emit();
      return;
    }

    this.isLoaded = true;
    this.currentSrc = bgUrl;

    const img = new Image();
    img.onload = () => {
      if (this.isDestroyed) return;
      if (this.overlayElement && bgUrl === this.currentSrc) {
        this.renderer.setStyle(this.overlayElement, 'background-image', `url('${ bgUrl }')`);

        requestAnimationFrame(() => {
          if (this.isDestroyed) return;
          if (this.overlayElement) {
            this.renderer.setStyle(this.overlayElement, 'opacity', '1');
          }
          this.removeSpinner();
          this.imageLoaded.emit();
        });
      }
    };

    img.onerror = () => {
      if (this.isDestroyed) return;
      this.removeSpinner();
      this.imageLoaded.emit();
    };

    img.src = bgUrl;
  }

  private renderStaticBackground(): void {
    const bgUrl = this.lazyBgLoader();
    if (!this.overlayElement || !bgUrl) {
      return;
    }

    this.isLoaded = true;
    this.currentSrc = bgUrl;
    this.renderer.setStyle(this.overlayElement, 'background-image', `url('${ bgUrl }')`);
    this.renderer.setStyle(this.overlayElement, 'opacity', '1');
    this.imageLoaded.emit();
  }
}
